import React, { createContext, ReactNode, useCallback, useContext, useMemo } from 'react';
import { useCardFormMetrics } from './CardFormMetricsContext';
import { useClientMetrics } from './ClientMetricsContext';
import { preInit } from './Api';
import { CardEvent, Metrics } from '../types/Metrics';
import { ICardTrackEvent } from '../components/card/wl/cardForm/CardFormWL.types';

export const mapToCardEvent = (source: ICardTrackEvent): CardEvent => ({
  event: String(source.event),
  isValid: source.isValid,
  fieldType: source.fieldType,
  reason: source.reason,
  timestamp: source.timestamp,
});

export interface AggregatedMetricsType {
  behaviorMetrics: object;
  sendMetrics: (paymentId: string) => Promise<void>;
  isReady: boolean;
}

interface AggregatedAntiFraudProviderProps {
  children: ReactNode;
}

export const AggregatedMetricsContext = createContext<AggregatedMetricsType | undefined>(undefined);

export function AggregatedMetricsProvider({ children }: AggregatedAntiFraudProviderProps) {
  const card = useCardFormMetrics();
  const client = useClientMetrics();

  const sendMetrics = useCallback(
    async (paymentId: string) => {
      const payload = {
        card: {
          cardEvents: card.events?.map(mapToCardEvent) ?? [],
          startTime: card.startTime,
        },
        behaviorScore: client.score || 0,
      } as Metrics;

      try {
        await preInit(paymentId, payload);
      } catch (error) {
        // skip error
      }
    },
    [card, client]
  );

  const value = useMemo(
    () => ({
      behaviorMetrics: client,
      sendMetrics,
      isReady: true,
    }),
    [card, client, sendMetrics]
  );

  return <AggregatedMetricsContext.Provider value={value}>{children}</AggregatedMetricsContext.Provider>;
}

export const useAggregatedMetrics = (): AggregatedMetricsType => {
  const context = useContext(AggregatedMetricsContext);
  if (context === undefined) {
    throw new Error('useAggregatedMetrics must be used within AggregatedAntiFraudProvider');
  }
  return context;
};
